// Где суда были — нарезка истории флота по моменту кадра.
//
// Ролик «Скачать вид → MP4» идёт по суткам окна перемотки: растр меняется на
// смене даты, косяки и выловы едут по трекам. Суда в нём стояли, и причина была
// не в записи, а в источнике: слой флота кормит `ais_vessel`, где лежит
// ПОСЛЕДНЯЯ известная позиция и ничего кроме неё. Кадр с датой «10 августа» нёс
// флот сегодняшний.
//
// Теперь запись один раз забирает историю за окно (`/fleet/vessels/history`) и
// режет её локально — тем же приёмом, что снос косяков (`models/driftDay.ts`):
// в цикле записи полторы сотни кадров, и запрос на каждый растянул бы её на
// минуты.
//
// Здесь только нарезка. Ни карта, ни кодировщик о модуле не знают.
import type { VideoFrameSpec } from './videoTimeline'
import type { VesselProps } from '../api'
import { msg, type Msg } from '../i18n'
import { dateFromOffset } from '../models/driftDay'

/** Точка трека: [мс UTC, долгота, широта, курс или null].
 *
 *  Плоский массив, а не объект: точек тысячи, и ключи на каждой утроили бы
 *  и ответ сервера, и разбор. */
export type FleetFix = [number, number, number, number | null]

export interface FleetHistoryVessel {
  /** Свойства живого слоя — их читают карточка судна и стиль маркера.
   *
   *  `Partial`, потому что набор задаёт СЕРВЕР, а не эта нарезка: она читает из
   *  него ровно `cog`, а остальное переносит как есть. Требуй мы здесь полный
   *  `VesselProps`, любое новое поле карточки ломало бы сборку модуля, который
   *  о карточке ничего не знает. */
  props: Partial<VesselProps>
  /** Позиция «как сейчас»: она же запасная для судна без истории. */
  position: [number, number]
  fixes: FleetFix[]
}

export interface FleetHistoryMeta {
  shown: number
  truncated: boolean
  /** Сколько судов реально поедет (больше одного фикса за окно). */
  with_history: number
}

export interface FleetHistory {
  vessels: FleetHistoryVessel[]
  meta: FleetHistoryMeta
}

const HOUR_MS = 3_600_000
const DAY_MS = 86_400_000

/** Потолок разрыва, внутри которого положение интерполируется, часы.
 *
 *  Судно, молчавшее трое суток, поехало бы по прямой через полокеана — ровно то
 *  выдуманное движение, ради отказа от которого источником выбрана история, а
 *  не счисление по курсу и скорости. За потолком держим последний известный
 *  фикс: «стоит, потому что молчит» — правда, а прямая линия — нет.
 *
 *  Шесть часов — это чуть больше четырёх кадров ролика: движение внутри допуска
 *  остаётся плавным, а молчание видно как остановку. */
export const GAP_MAX_H = 6

/** Возраст, после которого маркер сереет. Тот же порог, что у живого слоя
 *  (`STALE_AGE_HOURS` на бэкенде), — иначе одно и то же судно меняло бы цвет
 *  при переходе от экрана к ролику. */
export const STALE_AGE_H = 24

/** Момент кадра в мс UTC.
 *
 *  Час здесь НАСТОЯЩИЙ, и этим ролик про суда отличается от ролика про снос:
 *  там доля суток — лишь положение между двумя посчитанными за сутки узлами
 *  (течений мельче суток не существует), а АИС живёт в реальном времени. */
export function frameTimeMs(spec: VideoFrameSpec, today: string): number {
  const day = dateFromOffset(spec.dayOffset, today)
  const [y, m, d] = day.split('-').map(Number)
  return Date.UTC(y, m - 1, d) + spec.frac * DAY_MS
}

/** Долгота обратно в знаковую шкалу [−180, 180). */
function wrapLon(lon: number): number {
  return (((lon + 180) % 360) + 360) % 360 - 180
}

/** Интерполяция долготы ЧЕРЕЗ КОРОТКУЮ дугу.
 *
 *  В базе долгота знаковая, и 179,9° с −179,9° — соседи в двенадцати километрах
 *  друг от друга. Наивное `a + (b − a) · k` провезло бы судно через Атлантику за
 *  один кадр. Та же ошибка уже ловилась на решётках и на снапе CMEMS
 *  (`src/geo/dateline.py::to_east`) — здесь её клиентский близнец. */
function lerpLon(a: number, b: number, k: number): number {
  let delta = b - a
  if (delta > 180) delta -= 360
  else if (delta < -180) delta += 360
  return wrapLon(a + delta * k)
}

/** Направление отрезка, градусы по часовой от севера.
 *
 *  Это ИЗМЕРЕНИЕ, а не выдумка: между двумя реальными фиксами судно и правда
 *  сместилось так. Поэтому им заполняется только пустой курс и только на
 *  отрезке — у стоящего судна отрезка нет, и его «нет курса» остаётся. */
function segmentBearing(from: FleetFix, to: FleetFix): number | null {
  let dLon = to[1] - from[1]
  if (dLon > 180) dLon -= 360
  else if (dLon < -180) dLon += 360
  const dLat = to[2] - from[2]
  if (dLon === 0 && dLat === 0) return null
  // Косинус широты: градус долготы на 50° с.ш. короче градуса широты в полтора
  // раза, и без поправки курс уезжал бы тем сильнее, чем севернее судно.
  const x = dLon * Math.cos(((from[2] + to[2]) / 2) * (Math.PI / 180))
  const deg = (Math.atan2(x, dLat) * 180) / Math.PI
  return (deg + 360) % 360
}

/** Индекс последнего фикса не позже `t`; −1, если такого нет. Двоичный поиск:
 *  на кадр приходится до четырёхсот судов, и линейный проход по каждому
 *  превратил бы нарезку в заметную долю времени кадра. */
function lastAtOrBefore(fixes: FleetFix[], t: number): number {
  let lo = 0
  let hi = fixes.length - 1
  let found = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (fixes[mid][0] <= t) {
      found = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return found
}

/** Свежесть положения НА МОМЕНТ КАДРА.
 *
 *  Серый значит «на этот момент положение не подтверждено», и потому свежим
 *  считается только фикс, снятый не раньше суток ДО кадра. Фикс из будущего
 *  кадра (мы показываем судно там, куда оно ещё не пришло) свежим не считается:
 *  формально его возраст отрицателен, а по сути мы этого ещё не знали. */
function freshAt(fixMs: number | null, frameMs: number): boolean {
  if (fixMs == null) return false
  const age = frameMs - fixMs
  return age >= 0 && age <= STALE_AGE_H * HOUR_MS
}

/** Положение одного судна на момент кадра. */
function vesselAt(
  v: FleetHistoryVessel,
  t: number,
): { lon: number; lat: number; cog: number | null; fresh: boolean } {
  const { fixes } = v

  // Истории нет вовсе — рисуем там, где судно стоит на живой карте. Прятать
  // нельзя: за окно записи историю имеет меньшая часть флота, и без этого
  // правила ролик выглядел бы так, будто флот разбежался.
  if (fixes.length === 0) {
    const cog = typeof v.props.cog === 'number' ? (v.props.cog as number) : null
    return { lon: v.position[0], lat: v.position[1], cog, fresh: false }
  }

  const i = lastAtOrBefore(fixes, t)

  // Кадр раньше первого фикса. Показываем первую известную точку — судно,
  // выскакивающее посреди ролика, читается как поломка записи, а не как
  // отсутствие данных.
  if (i < 0) {
    const f = fixes[0]
    return { lon: f[1], lat: f[2], cog: f[3], fresh: false }
  }

  const cur = fixes[i]
  const next = i + 1 < fixes.length ? fixes[i + 1] : null

  // Последний фикс (в том числе на всех будущих датах окна) — замираем на нём.
  if (!next) {
    return { lon: cur[1], lat: cur[2], cog: cur[3], fresh: freshAt(cur[0], t) }
  }

  const span = next[0] - cur[0]
  if (span > GAP_MAX_H * HOUR_MS) {
    // Молчание длиннее допуска: держим прежнюю точку. Свежесть считается по
    // ней же — судно и правда не подтверждало положение всё это время.
    return { lon: cur[1], lat: cur[2], cog: cur[3], fresh: freshAt(cur[0], t) }
  }

  const k = span > 0 ? (t - cur[0]) / span : 0
  return {
    lon: lerpLon(cur[1], next[1], k),
    lat: cur[2] + (next[2] - cur[2]) * k,
    cog: cur[3] ?? segmentBearing(cur, next),
    // Внутри отрезка мы стоим между двумя подтверждениями — свежесть считаем по
    // тому, откуда едем.
    fresh: freshAt(cur[0], t),
  }
}

/** Что сказать про флот в подписи охвата.
 *
 *  Молчать нельзя ни о том, ни о другом. Неподвижные суда в ролике читаются как
 *  поломка записи, хотя означают ровно «эти молчали в эфире»; обрезка потолком
 *  без строки в подписи означала бы, что в кадре весь флот.
 *
 *  Возвращает `Msg` — ключ каталога и подстановки, а не готовую фразу: иначе
 *  тест этого правила стал бы вторым, неявным каталогом переводов. */
export function fleetCoverageNotes(meta: FleetHistoryMeta | null): Msg[] {
  if (!meta || meta.shown === 0) return []
  const out: Msg[] = [
    msg('map.export.fleetMoving', { moving: meta.with_history, shown: meta.shown }),
  ]
  if (meta.truncated) out.push(msg('map.export.fleetTruncated', { shown: meta.shown }))
  return out
}

/** Ответы по сегментам (свой / РФ / ин.флот) → один набор.
 *
 *  Дедуп по MMSI с приоритетом РАНЬШЕ пришедшего — сегменты приходят в
 *  каноническом порядке (own → nat → foreign), и копия из `own` перекрывает
 *  остальные: только в ней есть юр.лицо. Та же дисциплина, что у живого слоя
 *  (`useFleetLayer`), и разойтись им нельзя — иначе одно судно в ролике и на
 *  экране показывало бы в карточке разное.
 *
 *  Упавший сегмент приезжает как `null` и просто не участвует: отсутствие
 *  иностранцев не повод выбрасывать свой флот.
 */
export function mergeFleetHistory(parts: Array<FleetHistory | null>): FleetHistory {
  const byKey = new Map<string, FleetHistoryVessel>()
  let truncated = false
  for (const part of parts) {
    if (!part) continue
    truncated = truncated || Boolean(part.meta?.truncated)
    for (const v of part.vessels) {
      const key = String(v.props.mmsi ?? '') || `@${v.position.join(',')}`
      if (!byKey.has(key)) byKey.set(key, v)
    }
  }
  const vessels = [...byKey.values()]
  return {
    vessels,
    meta: {
      shown: vessels.length,
      truncated,
      with_history: vessels.filter(v => v.fixes.length > 1).length,
    },
  }
}

/** История флота → коллекция для слоя `vessels` на момент `timeMs`.
 *
 *  Формат ответа совпадает с живым `/fleet/vessels`: тот же слой, тот же стиль
 *  маркера (`vesselIconImage` читает `cog` и `stale`), та же карточка по клику.
 *  Свой, второй формат дал бы флот, который едет, но при клике показывает
 *  пустую карточку. */
export function sliceFleetAt(history: FleetHistory, timeMs: number): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: history.vessels.map(v => {
      const at = vesselAt(v, timeMs)
      return {
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [at.lon, at.lat] },
        properties: {
          ...v.props,
          // Курс и свежесть — ЭТОГО момента, а не сегодняшнего: иначе судно
          // ехало бы задом наперёд и светилось свежим на кадре недельной
          // давности.
          cog: at.cog,
          stale: !at.fresh,
        },
      }
    }),
  }
}
